import React, { useState } from 'react';
import {
    ButtonGroup,
    Tooltip,
    IconButton,
    Divider,
    Menu,
    MenuItem,
    Popover,
    TextField,
    Box,
    Typography
} from '@mui/material';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    Code,
    FormatQuote,
    FormatListBulleted,
    FormatListNumbered,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    FormatAlignJustify,
    Link,
    InsertPhoto,
    Title,
    StrikethroughS,
    FormatColorText,
    Subscript,
    Superscript,
    HorizontalRule,
    FormatClear,
    HighlightAlt,
    FormatIndentIncrease,
    FormatIndentDecrease,
    FormatLineSpacing,
    FontDownload,
    TableChart,
    CheckBox,
    FormatPaint,
    Undo,
    Redo,
    FormatSize,
    BorderColor,
    Functions,
    FormatShapes,
    FormatStrikethrough,
} from '@mui/icons-material';

const Toolbar = ({ editor }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [linkUrl, setLinkUrl] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
    const [highlightColor, setHighlightColor] = useState('#FFFF00');
    const [fontFamily, setFontFamily] = useState('Arial');
    const [fontSize, setFontSize] = useState('16px');
    const [tableRows, setTableRows] = useState(3);
    const [tableCols, setTableCols] = useState(3);

    if (!editor) {
        return null;
    }

    const open = Boolean(anchorEl);
    const colorPickerOpen = Boolean(colorPickerAnchor);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColorPickerClick = (event) => {
        setColorPickerAnchor(event.currentTarget);
    };

    const handleColorPickerClose = () => {
        setColorPickerAnchor(null);
    };

    const addLink = () => {
        if (linkUrl) {
            editor.chain().focus().setLink({ href: linkUrl }).run();
        }
        setLinkUrl('');
        handleClose();
    };

    const addImage = () => {
        if (imageUrl) {
            editor.chain().focus().setImage({ src: imageUrl }).run();
        }
        setImageUrl('');
        handleClose();
    };

    const insertTable = () => {
        editor.chain().focus()
            .insertTable({ rows: tableRows, cols: tableCols, withHeaderRow: true })
            .run();
        handleClose();
    };

    const presetColors = [
        '#000000', '#ffffff', '#ff0000', '#00ff00', '#0000ff',
        '#ffff00', '#00ffff', '#ff00ff', '#c0c0c0', '#808080'
    ];

    const highlightColors = [
        '#FFFF00', '#FFCC00', '#FF9900', '#FF6600', '#FF0000',
        '#FF99CC', '#FF00FF', '#CC00FF', '#9900FF', '#00FF00'
    ];

    const fontFamilies = [
        'Arial', 'Verdana', 'Times New Roman', 'Courier New',
        'Georgia', 'Palatino', 'Garamond', 'Comic Sans MS',
        'Impact', 'Lucida Sans Unicode'
    ];

    const fontSizes = ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px', '48px'];

    return (
        <Box sx={{
            marginBottom: '20px',
            padding: '10px',
            background: 'rgba(30, 30, 30, 0.8)',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            border: '1px solid #444',
        }}>
            {/* First Row - Basic Formatting */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Undo">
                        <IconButton onClick={() => editor.chain().focus().undo().run()}>
                            <Undo fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Redo">
                        <IconButton onClick={() => editor.chain().focus().redo().run()}>
                            <Redo fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Bold">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            color={editor.isActive('bold') ? 'primary' : 'default'}
                        >
                            <FormatBold fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Italic">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            color={editor.isActive('italic') ? 'primary' : 'default'}
                        >
                            <FormatItalic fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Underline">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                            color={editor.isActive('underline') ? 'primary' : 'default'}
                        >
                            <FormatUnderlined fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Strikethrough">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleStrike().run()}
                            color={editor.isActive('strike') ? 'primary' : 'default'}
                        >
                            <FormatStrikethrough fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Heading 1">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                            color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}
                        >
                            <Title fontSize="small" /> H1
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Heading 2">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                            color={editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'}
                        >
                            <Title fontSize="small" /> H2
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Heading 3">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                            color={editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'}
                        >
                            <Title fontSize="small" /> H3
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Bullet List">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                            color={editor.isActive('bulletList') ? 'primary' : 'default'}
                        >
                            <FormatListBulleted fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Numbered List">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            color={editor.isActive('orderedList') ? 'primary' : 'default'}
                        >
                            <FormatListNumbered fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Task List">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleTaskList().run()}
                            color={editor.isActive('taskList') ? 'primary' : 'default'}
                        >
                            <CheckBox fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>
            </Box>

            {/* Second Row - Advanced Formatting */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Blockquote">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleBlockquote().run()}
                            color={editor.isActive('blockquote') ? 'primary' : 'default'}
                        >
                            <FormatQuote fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Code Block">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                            color={editor.isActive('codeBlock') ? 'primary' : 'default'}
                        >
                            <Code fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Horizontal Rule">
                        <IconButton onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                            <HorizontalRule fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Align Left">
                        <IconButton
                            onClick={() => editor.chain().focus().setTextAlign('left').run()}
                            color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
                        >
                            <FormatAlignLeft fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Align Center">
                        <IconButton
                            onClick={() => editor.chain().focus().setTextAlign('center').run()}
                            color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
                        >
                            <FormatAlignCenter fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Align Right">
                        <IconButton
                            onClick={() => editor.chain().focus().setTextAlign('right').run()}
                            color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
                        >
                            <FormatAlignRight fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Justify">
                        <IconButton
                            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                            color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
                        >
                            <FormatAlignJustify fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Increase Indent">
                        <IconButton onClick={() => editor.chain().focus().sinkListItem('listItem').run()}>
                            <FormatIndentIncrease fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Decrease Indent">
                        <IconButton onClick={() => editor.chain().focus().liftListItem('listItem').run()}>
                            <FormatIndentDecrease fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>
            </Box>

            {/* Third Row - Colors, Links, Images, Tables */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center' }}>
                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Text Color">
                        <IconButton onClick={handleColorPickerClick}>
                            <FormatColorText fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Highlight">
                        <IconButton onClick={() => editor.chain().focus().toggleHighlight({ color: highlightColor }).run()}>
                            <BorderColor fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Clear Format">
                        <IconButton onClick={() => editor.chain().focus().unsetAllMarks().run()}>
                            <FormatClear fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Insert Link">
                        <IconButton onClick={handleClick}>
                            <Link fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Insert Image">
                        <IconButton onClick={() => {
                            const url = window.prompt('Enter the URL of the image:');
                            if (url) {
                                editor.chain().focus().setImage({ src: url }).run();
                            }
                        }}>
                            <InsertPhoto fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Insert Table">
                        <IconButton onClick={() => {
                            const rows = parseInt(prompt('Enter number of rows:', '3'));
                            const cols = parseInt(prompt('Enter number of columns:', '3'));
                            if (rows && cols) {
                                editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
                            }
                        }}>
                            <TableChart fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Subscript">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleSubscript().run()}
                            color={editor.isActive('subscript') ? 'primary' : 'default'}
                        >
                            <Subscript fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Superscript">
                        <IconButton
                            onClick={() => editor.chain().focus().toggleSuperscript().run()}
                            color={editor.isActive('superscript') ? 'primary' : 'default'}
                        >
                            <Superscript fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <ButtonGroup variant="outlined" size="small">
                    <Tooltip title="Font Family">
                        <IconButton onClick={() => {
                            const family = prompt('Enter font family (e.g., Arial, Times New Roman):', fontFamily);
                            if (family) {
                                setFontFamily(family);
                                editor.chain().focus().setFontFamily(family).run();
                            }
                        }}>
                            <FontDownload fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Font Size">
                        <IconButton onClick={() => {
                            const size = prompt('Enter font size (e.g., 12px, 1.2em):', fontSize);
                            if (size) {
                                setFontSize(size);
                                editor.chain().focus().setFontSize(size).run();
                            }
                        }}>
                            <FormatSize fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </ButtonGroup>
            </Box>

            {/* Link Menu */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="URL"
                        type="url"
                        fullWidth
                        variant="outlined"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addLink()}
                    />
                </MenuItem>
                <MenuItem onClick={addLink}>
                    Insert Link
                </MenuItem>
            </Menu>

            {/* Color Picker Popover */}
            <Popover
                open={colorPickerOpen}
                anchorEl={colorPickerAnchor}
                onClose={handleColorPickerClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ p: 2, width: 200 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">Text Color</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {presetColors.map((color) => (
                                <IconButton
                                    key={color}
                                    sx={{
                                        backgroundColor: color,
                                        width: 24,
                                        height: 24,
                                        border: '1px solid #ccc',
                                        '&:hover': { border: '1px solid #fff' }
                                    }}
                                    onClick={() => {
                                        editor.chain().focus().setColor(color).run();
                                        handleColorPickerClose();
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Highlight Color</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {highlightColors.map((color) => (
                                <IconButton
                                    key={color}
                                    sx={{
                                        backgroundColor: color,
                                        width: 24,
                                        height: 24,
                                        border: '1px solid #ccc',
                                        '&:hover': { border: '1px solid #fff' }
                                    }}
                                    onClick={() => {
                                        setHighlightColor(color);
                                        editor.chain().focus().toggleHighlight({ color }).run();
                                        handleColorPickerClose();
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default Toolbar;